import type { ReactNode } from 'react';
import { ThemeToggle } from './ThemeToggle';
import { BuildStamp } from './BuildStamp';

/**
 * Login / signup / invite-landing layout. Mirrors the legacy
 * `Docs/Web/User/screenshots/desktop_root.png` design:
 *
 *   ┌──────────────────────┬──────────────────────┐
 *   │  AquaStar wordmark   │                      │
 *   │                      │   [lifestyle photo   │
 *   │  Welcome to ...      │    with brand        │
 *   │  [email]             │    gradient overlay] │
 *   │  [password]          │                      │
 *   │  [Sign in]           │                      │
 *   └──────────────────────┴──────────────────────┘
 *
 * Collapses to single-column under Bootstrap's `lg` breakpoint
 * (≤992px). Hero pane drops on mobile.
 *
 * The form pane includes a subtle theme toggle in the top-right
 * corner so users on the public pages can switch dark/light without
 * needing to be signed in.
 */
export function TwoPaneLayout({
  children,
  hero,
}: {
  children: ReactNode;
  /**
   * Optional hero override — defaults to the lifestyle photo with
   * brand gradient overlay + AquaStar wordmark. Pass a JSX element to
   * render alternative content over the gradient.
   */
  hero?: ReactNode;
}) {
  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: '100vh', background: 'var(--aqua-bg)' }}
    >
      <div className="row g-0" style={{ minHeight: '100vh' }}>
        {/* Form pane */}
        <div
          className="col-12 col-lg-6 d-flex align-items-center justify-content-center p-4 p-md-5 position-relative"
          style={{ background: 'var(--aqua-surface)' }}
        >
          <div
            className="position-absolute"
            style={{ top: 16, right: 16, zIndex: 5 }}
          >
            <ThemeToggle />
          </div>

          <div style={{ width: '100%', maxWidth: 440 }}>
            {/* AquaStar logo above the form. Uses the same brand logo
                file as the admin console (Web/Admin/public/aquastar-logo.png)
                so the two products feel like one brand. The previous
                icon + "AquaStar / Pool Products" text label was a
                placeholder until the licensed logo art was vetted. */}
            <div className="mb-4">
              <img
                src="/aquastar-logo.png"
                alt="AquaStar"
                style={{
                  display: 'block',
                  maxWidth: 240,
                  width: '100%',
                  height: 'auto',
                }}
                onError={(e) => {
                  // Fallback if the asset is missing — degrade to the
                  // wordmark we shipped before so the page still reads.
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>

            {children}

            {/* P1-WU06 — small footer with the current build's short SHA,
                tag and date. Lives in the layout shared by login /
                signup / invite-landing pages so it's visible on every
                splash surface but doesn't crowd the authed dashboard. */}
            <BuildStamp />
          </div>
        </div>

        {/* Hero pane — lifestyle photo + brand-gradient overlay */}
        <div
          className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center p-5 text-white position-relative"
          style={{
            backgroundColor: 'var(--aqua-navy)',
            backgroundImage: `
              linear-gradient(135deg,
                rgba(16, 28, 66, 0.55) 0%,
                rgba(12, 60, 96, 0.40) 45%,
                rgba(1, 157, 220, 0.35) 100%),
              url('/auth-bg.png')
            `,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {hero ?? <DefaultHero />}
        </div>
      </div>
    </div>
  );
}

function DefaultHero() {
  return (
    <div
      className="text-center position-relative"
      style={{ maxWidth: 480, zIndex: 2 }}
    >
      <div
        style={{
          textShadow: '0 2px 12px rgba(0, 0, 0, 0.35)',
        }}
      >
        <h1 className="display-4 fw-bold mb-3" style={{ letterSpacing: -1 }}>
          AquaStar
        </h1>
        <p
          className="lead mb-0"
          style={{
            color: 'rgba(255, 255, 255, 0.92)',
            fontWeight: 400,
            fontSize: '1.25rem',
          }}
        >
          Smarter, quieter pool pumps —
          <br />
          controlled from anywhere.
        </p>
      </div>
    </div>
  );
}
